import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { Form, Formik } from "formik";
import * as yup from "yup";
import React from "react";
import { useUpdateBenutzerAzsaldo } from "../../client/hooks";
import { useMutation, useQueryClient } from "react-query";
import { AzSaldoDaten, Benutzer } from "../../api";
import { enqueueSnackbar } from "notistack";
import { useT } from "../../i18n";
import AzSaldoFields from "./AzSaldoFields";
import EditIcon from "@mui/icons-material/Edit";

export default function UpdateAzSaldoAnwesenheitButton({
  title,
  selectedMitarbeiterId,
}: {
  title: string;
  selectedMitarbeiterId?: string;
}) {
  const { t } = useT();
  const { t: t1 } = useT("benutzer");

  const mutate = useUpdateAzSaldo();
  const saldo = title.trim() === "-" ? 0 : parseFloat(title.replace(",", "."));

  const popupState = usePopupState({
    variant: "popover",
    popupId: `azsaldo-${selectedMitarbeiterId}-update`,
  });
  const schema = yup.object().shape({
    mitarbeiterId: yup
      .object()
      .shape({
        id: yup.string().required(),
      })
      .nullable()
      .required(t("required-field")),
    azSaldo: yup.number().required(t("required-field")),
  });

  const onSubmit = async (values: {
    azSaldo: number;
    mitarbeiterId: Benutzer | string | undefined;
  }) => {
    if (values.mitarbeiterId) {
      const mitarbeiter = values.mitarbeiterId as unknown as Benutzer;
      if (mitarbeiter.id) {
        try {
          await mutate.mutateAsync({
            benutzerId: mitarbeiter.id,
            azSaldo: values.azSaldo,
          });
          enqueueSnackbar(t1("azsaldo-saved"), { variant: "success" });
          popupState.close();
        } catch (error) {
          enqueueSnackbar(t1("azsaldo-error"), { variant: "error" });
        }
      }
    }
  };
  return (
    <>
      <IconButton
        {...bindTrigger(popupState)}
        aria-label="edit"
        sx={{ color: "black" }}
      >
        <EditIcon />
      </IconButton>
      <Dialog {...bindPopover(popupState)}>
        <DialogTitle id="alert-dialog-title">{t1("azsaldo-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Formik
              enableReinitialize={true}
              validationSchema={schema}
              initialValues={{
                azSaldo: saldo,
                mitarbeiterId: selectedMitarbeiterId,
              }}
              onSubmit={onSubmit}
            >
              {({ isSubmitting, isValid, dirty, touched, errors }) => {
                return (
                  <>
                    <Form id="azsaldo">
                      <Box sx={{ mt: 1 }}></Box>
                      <AzSaldoFields
                        mitarbeiterIdProp={selectedMitarbeiterId}
                      />
                      <DialogActions>
                        <Button
                          onClick={() => popupState.close()}
                          color="inherit"
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          disabled={isSubmitting || !dirty || !isValid}
                        >
                          {t("save")}
                        </Button>
                      </DialogActions>
                    </Form>
                  </>
                );
              }}
            </Formik>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
}
function useUpdateAzSaldo() {
  const updateAzSaldo = useUpdateBenutzerAzsaldo();
  const queryClient = useQueryClient();

  return useMutation(
    (input: AzSaldoDaten) => {
      return updateAzSaldo(input);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(["azsaldo"]);
      },
      onError: (error) => {
        console.error("Failed to update AZ saldo:", error);
      },
    }
  );
}
